import {css, StyleSheet} from 'aphrodite';
import React, {useMemo} from 'react';

import {Artifact, ArtifactComponentProps, ArtifactType} from 'llm/chat-library/ArtifactTypes';
import {FONT_PROXIMA_14, FONT_WEIGHT_PROXIMA_SEMIBOLD} from 'styles/FontConstants';
import {NEAR_BLACK} from 'ui-library/Colors';

/** Shape of the analysis_plan field in application state from the backend. */
interface AnalysisPlanState {
  problem_statement: string;
  plan_steps: Array<string>;
}

export interface PlanArtifactData {
  problemStatement: string;
  planSteps: Array<string>;
}
export type PlanArtifact = Artifact<PlanArtifactData>;

/**
 * Component that renders a plan artifact.
 * Renders the problem statement and numbered plan steps as plain text.
 */
function PlanArtifactComponent({data}: ArtifactComponentProps<PlanArtifactData>): React.ReactElement {
  return (
    <div className={css(styles.planContainer)} aria-label="Analysis plan">
      <p className={css(styles.problemStatement)}>{data.problemStatement}</p>
      {data.planSteps.length > 0 && (
        <div>
          <p className={css(styles.stepsLabel)}>Plan steps:</p>
          <ol className={css(styles.stepsList)} aria-label="Plan steps">
            {data.planSteps.map((step, i) => (
              <li key={i} className={css(styles.step)}>
                {step}
              </li>
            ))}
          </ol>
        </div>
      )}
    </div>
  );
}

function hasAnalysisPlan(state: object): state is {analysis_plan: AnalysisPlanState} {
  // eslint-disable-next-line benchlint/no-in-operator
  return 'analysis_plan' in state && (state as {analysis_plan: unknown}).analysis_plan != null;
}

/**
 * Hook for managing a plan artifact in the chat.
 *
 * Extracts `analysis_plan` from application state (a structured object with
 * `problem_statement` and `plan_steps`) and provides a component to render it.
 *
 * @returns The artifact, or null if no plan exists
 */
export function usePlanArtifact({
  latestApplicationState,
}: {
  latestApplicationState: object | null;
}): PlanArtifact | null {
  return useMemo((): PlanArtifact | null => {
    if (!latestApplicationState || !hasAnalysisPlan(latestApplicationState)) {
      return null;
    }
    const plan = latestApplicationState.analysis_plan;
    if (!plan.problem_statement && plan.plan_steps.length === 0) {
      return null;
    }
    return {
      artifactType: ArtifactType.PLAN,
      open: () => {},
      data: {
        problemStatement: plan.problem_statement,
        planSteps: plan.plan_steps,
      },
      Component: PlanArtifactComponent,
    };
  }, [latestApplicationState]);
}

const styles = StyleSheet.create({
  planContainer: {
    padding: 16,
  },
  problemStatement: {
    ...FONT_PROXIMA_14,
    color: NEAR_BLACK,
    margin: '0 0 12px 0',
    lineHeight: '1.5',
  },
  stepsLabel: {
    ...FONT_PROXIMA_14,
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
    color: NEAR_BLACK,
    margin: '0 0 8px 0',
  },
  stepsList: {
    ...FONT_PROXIMA_14,
    color: NEAR_BLACK,
    margin: 0,
    paddingLeft: 20,
    lineHeight: '1.5',
  },
  step: {
    color: NEAR_BLACK,
    marginBottom: 4,
  },
});
